import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, zip } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DateService, DeviceService, DialogService, HttpService, ScreenSize, TranslateService } from 'vas-mobile-api';

import { FieldType, Operator, Option } from '../models/chips-and-fields';
import { Parameter, Report, ReportFilter, Reports, ReportTask } from '../models/report';
import { DataExchangeService } from '../services/dataexchange.service';
import { PrintService } from '../services/print.service';

@Component({
  selector: 'app-report-detail',
  templateUrl: './report-detail.component.html',
  styleUrls: ['./report-detail.component.css']
})
export class ReportDetailComponent implements OnInit, OnDestroy {

  // a little hack for the HTML
  public fieldType = FieldType;
  public reportTask = ReportTask;

  public filterForm: FormGroup;
  public report: Partial<Report> = {};
  public mobileView: boolean;
  private ngUnsubscribe = new Subject<void>();
  public visibleParams: Array<Parameter> = [];
  public groupAParams: Array<Parameter> = [];
  public groupBParams: Array<Parameter> = [];
  public showLoading: boolean;

  constructor(
    private httpService: HttpService,
    private route: ActivatedRoute,
    private deviceService: DeviceService,
    private des: DataExchangeService,
    private dateService: DateService,
    private router: Router,
    private printService: PrintService,
    private dialogService: DialogService,
    private translateService: TranslateService) { }

  ngOnInit() {

    this.filterForm = new FormGroup({});

    const config$ = this.httpService.getLocalResource('assets/config/reportsConfig.json');
    const routeParams$ = this.route.paramMap.pipe(takeUntil(this.ngUnsubscribe));

    zip(config$, routeParams$).subscribe((results) => {
      const config: Reports = results[0];
      const paramMap = results[1];

      this.report = config.reports.find((r) => r.id === paramMap.get('reportId'));

      if (this.report) {
        this.visibleParams = this.report.parameters.filter((p) => !p.invisible);
        this.visibleParams.forEach((field) => {
          let defaultOption: Option;
          let defaultValue = field.defaultValue;
          if (field.options && (defaultOption = field.options.find((o) => {
            return o.selected;
          }))) {
            defaultValue = defaultOption.key;
          }

          defaultValue = this.checkForSpecialValue(defaultValue);

          this.filterForm.addControl(field.formName,
            new FormControl({ value: defaultValue, disabled: field.disabled }, field.required ? Validators.required : null));

          if (field.keyCode) {
            if (!field.options) {
              field.options = [];
              field.options.push({ key: null, value: null });
            }
            this.httpService.getKeycodesGetAllKeysAndStrings(field.keyCode, true, true).subscribe((response) => {
              const keycode = response.data[field.keyCode];
              Object.keys(keycode).forEach((key) => {
                const value = keycode[key];
                field.options.push({ key, value });
              });
            });
          }
        });

        this.visibleParams = this.visibleParams.sort((a, b) => {
          if (!b.order && a.order || a.order < b.order) {
            return -1;
          } else if (!a.order && b.order || a.order > b.order) {
            return 1;
          }
          return 0;
        });

        this.groupAParams = this.visibleParams.filter((p) => {
          return p.type === FieldType.DATE || p.type === FieldType.INPUT || p.type == null;
        });

        this.groupBParams = this.visibleParams.filter((p) => {
          return p.type === FieldType.RADIO || p.type === FieldType.SELECT || p.type === FieldType.CHECKBOX;
        });
      }

    });


    this.deviceService.size.pipe(takeUntil(this.ngUnsubscribe)).subscribe((size) => {
      this.mobileView = size <= ScreenSize.sm;
    });

    this.des.back$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {

      this.router.navigate(['reportsList']);

    });

    this.des.changeTitelBarName('VASTREE_DMTN4');
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public onSubmit(task: ReportTask = ReportTask.PDF) {
    const formValue = this.filterForm.value;
    const filters = this.buildFilters(formValue);
    if (this.report.retrieveMethod === 'ID') {
      this.showLoading = true;
      this.printService.printReport(this.report.id, null, task, filters, this.mobileView).subscribe(() => {
        this.showLoading = false;
      }, (e) => {
        //  alert('Hello World-------------------');
          this.des.showLoadingIndicator(false);
          this.dialogService.openConfirmDialog(
            this.translateService.translateFirstLetterUpperCase('LBLPANEL_ERROR_HTTP'),
            this.translateService.translateFirstLetterUpperCase('LBLPANEL_TIMEOUT_REQUEST'),
            this.translateService.translateFirstLetterUpperCase('LBLPANEL_CONFIRM'));
          });
    } else if (this.report.retrieveMethod === 'FILENAME') {
      this.showLoading = true;
      this.printService.printReport(null, this.report.fileName, task, filters, this.mobileView).subscribe(() => {
        this.showLoading = false;
      });
    } else {
      throw new Error('Report must have a retrieve Method of ID or FILENAME');
    }
  }

  private checkForSpecialValue(value: string): any {
    if (value === '{{TODAY}}') {
      return this.dateService.getDate(new Date());
    }
    return value;
  }

  private buildFilters(formValue: any): Array<ReportFilter> {
    const filters: Array<ReportFilter> = [];
    this.report.parameters.forEach((param) => {
      let value = formValue[param.formName] || param.defaultValue;
      let sqlRestriction = param.sqlRestriction;

      // handle wildcard
      if (param.operator === Operator.LIKE && value) {
        value = value.replace('*', '%');
      }

      // handle checkbox
      if (param.type === FieldType.CHECKBOX) {
        value = value ? 'Y' : 'N';
      }

      // handle date
      if (param.type === FieldType.DATE && value) {
        value = this.dateService.getDate(new Date(value));
      }

      // handle select / radio SqlRestriction
      if (param.type === FieldType.RADIO || param.type === FieldType.SELECT) {
        const selectedOption = param.options.find((option) => {
          return option.key === value || option.key == null && value == null;
        });
        if (selectedOption.sqlRestriction) {
          sqlRestriction = selectedOption.sqlRestriction;
        }
      }

      const filter: ReportFilter = {
        key: sqlRestriction || null,
        name: param.parameterName || param.formName,
        value: value,
        operator: param.operator || null,
        type: param.type || FieldType.INPUT
      };
      filters.push(filter);
    });
    return filters;
  }

}
